/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { Baby, GraduationCap, Heart, type LucideIcon } from "lucide-react";
import type { SafeCircleMode, PrivacyLevel, Relationship } from "@/types/safecircle";

export interface ModeConfig {
  label: string;
  emoji: string;
  icon: LucideIcon;
  color: string;
  bgClass: string;
  textClass: string;
  borderClass: string;
  ringClass: string;
  gradientFrom: string;
  gradientTo: string;
  description: string;
  tagline: string;
}

export const MODE_CONFIG: Record<SafeCircleMode, ModeConfig> = {
  protected: {
    label: "Protected",
    emoji: "\u{1F9D2}",
    icon: Baby,
    color: "emerald",
    bgClass: "bg-emerald-500/15",
    textClass: "text-emerald-400",
    borderClass: "border-emerald-500/30",
    ringClass: "ring-emerald-500/50",
    gradientFrom: "from-emerald-500/20",
    gradientTo: "to-emerald-600/5",
    description: "safeCircle.modeProtectedDesc",
    tagline: "safeCircle.modeProtectedTagline",
  },
  connected: {
    label: "Connected",
    emoji: "\u{1F393}",
    icon: GraduationCap,
    color: "blue",
    bgClass: "bg-blue-500/15",
    textClass: "text-blue-400",
    borderClass: "border-blue-500/30",
    ringClass: "ring-blue-500/50",
    gradientFrom: "from-blue-500/20",
    gradientTo: "to-blue-600/5",
    description: "safeCircle.modeConnectedDesc",
    tagline: "safeCircle.modeConnectedTagline",
  },
  private: {
    label: "Private",
    emoji: "\u{1F49C}",
    icon: Heart,
    color: "purple",
    bgClass: "bg-purple-500/15",
    textClass: "text-purple-400",
    borderClass: "border-purple-500/30",
    ringClass: "ring-purple-500/50",
    gradientFrom: "from-purple-500/20",
    gradientTo: "to-purple-600/5",
    description: "safeCircle.modePrivateDesc",
    tagline: "safeCircle.modePrivateTagline",
  },
};

export const DEFAULT_PRIVACY_BY_MODE: Record<SafeCircleMode, PrivacyLevel> = {
  protected: "full_visibility",
  connected: "summary_only",
  private: "self_sovereign",
};

export const PRIVACY_DESCRIPTIONS: Record<PrivacyLevel, string> = {
  full_visibility: "safeCircle.privacyFull",
  summary_only: "safeCircle.privacySummary",
  self_sovereign: "safeCircle.privacySelfSovereign",
};

export const RELATIONSHIPS_BY_MODE: Record<SafeCircleMode, Relationship[]> = {
  protected: ["self", "child", "other"],
  connected: ["self", "teen", "other"],
  private: ["self", "spouse", "friend", "other"],
};

export const RELATIONSHIP_LABELS: Record<Relationship, string> = {
  self: "Myself",
  child: "My Child",
  teen: "My Teen",
  spouse: "My Spouse / Partner",
  friend: "A Friend",
  other: "Other",
};

export const AVATAR_COLORS = [
  "#10b981", // emerald
  "#3b82f6", // blue
  "#a855f7", // purple
  "#f59e0b", // amber
  "#ef4444", // red
  "#06b6d4", // cyan
  "#ec4899", // pink
  "#f97316", // orange
];
